import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes, FaTrash } from 'react-icons/fa';
import { getCart, removeFromCart, getNumericPrice } from '../utils/cartUtils';
import { useToast } from './ToastProvider';
import './MiniCartDrawer.css';

const MiniCartDrawer = ({ isOpen, onClose }) => {
  const [cartItems, setCartItems] = useState([]);
  const navigate = useNavigate();
  const drawerRef = useRef(null);
  const previousActiveElement = useRef(null);
  const { showToast } = useToast();

  useEffect(() => {
    if (isOpen) {
      // Store the previously focused element
      previousActiveElement.current = document.activeElement;

      // Update cart items when drawer opens
      setCartItems(getCart());

      // Focus trap: focus the first focusable element in the drawer
      const firstFocusable = drawerRef.current?.querySelector(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (firstFocusable) {
        firstFocusable.focus();
      }
    } else {
      // Restore focus when drawer closes
      if (previousActiveElement.current) {
        previousActiveElement.current.focus();
      }
    }
  }, [isOpen]);

  useEffect(() => {
    const updateCart = () => {
      setCartItems(getCart());
    };

    // Listen for cart updates
    window.addEventListener('cartUpdated', updateCart);
    window.addEventListener('storage', updateCart);

    // Initial load
    updateCart();

    return () => {
      window.removeEventListener('cartUpdated', updateCart);
      window.removeEventListener('storage', updateCart);
    };
  }, []);

  // Handle keyboard navigation (Escape key to close) and body scroll lock
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when drawer is open
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  // Focus trap: keep focus within drawer
  useEffect(() => {
    if (!isOpen) return;

    const handleTabKey = (e) => {
      if (e.key !== 'Tab') return;

      const focusableElements = drawerRef.current?.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );

      if (!focusableElements || focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (e.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        // Tab
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };

    document.addEventListener('keydown', handleTabKey);
    return () => document.removeEventListener('keydown', handleTabKey);
  }, [isOpen]);

  const handleRemove = (id, e) => {
    e.stopPropagation();
    removeFromCart(id);
    setCartItems(getCart());
    window.dispatchEvent(new Event('cartUpdated'));
    showToast('Removed from cart', 'info');
  };

  const calculateTotal = () => {
    return cartItems.reduce((sum, item) => {
      const price = getNumericPrice(item.price);
      return sum + (item.quantity * price);
    }, 0);
  };

  const handleViewCart = () => {
    onClose();
    navigate('/cart');
  };

  const handleCheckout = () => {
    onClose();
    navigate('/cart');
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Overlay - CSS handles responsive positioning via media query */}
          <motion.div
            className="minicart-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={handleOverlayClick}
            aria-hidden="true"
          />

          {/* Drawer - CSS handles responsive positioning via media query */}
          <motion.div
            ref={drawerRef}
            className="minicart-drawer"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.3, ease: 'easeInOut' }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="minicart-title"
            aria-hidden={!isOpen}
          >
            {/* Header */}
            <div className="minicart-header">
              <h2 id="minicart-title" className="minicart-title">
                Shopping Cart
              </h2>
              <motion.button
                className="minicart-close"
                onClick={onClose}
                aria-label="Close cart"
                whileHover={{ scale: 1.1, rotate: 90 }}
                whileTap={{ scale: 0.9 }}
              >
                <FaTimes />
              </motion.button>
            </div>

            {/* Cart Items */}
            <div className="minicart-content">
              {cartItems.length === 0 ? (
                <div className="minicart-empty">
                  <p>Your cart is empty</p>
                  <p className="minicart-empty-subtitle">Add some beautiful items!</p>
                </div>
              ) : (
                <div className="minicart-items">
                  {cartItems.map((item) => {
                    const price = getNumericPrice(item.price);
                    const itemTotal = item.quantity * price;

                    return (
                      <motion.div
                        key={item.cartId || item.id}
                        className="minicart-item"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        <div className="minicart-item-image">
                          <img src={item.image} alt={item.name} loading="lazy" />
                        </div>
                        <div className="minicart-item-details">
                          <h3 className="minicart-item-title">{item.name}</h3>
                          {item.selectedVariant && (
                            <div className="minicart-item-variant">
                              {item.selectedVariant.label}
                            </div>
                          )}
                          <div className="minicart-item-meta">
                            <span className="minicart-item-price">₹{price} each</span>
                            <span className="minicart-item-quantity">Qty: {item.quantity}</span>
                          </div>
                          <div className="minicart-item-total">
                            Total: <strong>₹{itemTotal}</strong>
                          </div>
                        </div>
                        <motion.button
                          className="minicart-item-remove"
                          onClick={(e) => handleRemove(item.cartId || item.id, e)}
                          aria-label={`Remove ${item.name} from cart`}
                          whileHover={{ scale: 1.1, rotate: 5 }}
                          whileTap={{ scale: 0.9 }}
                        >
                          <FaTrash />
                        </motion.button>
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            {cartItems.length > 0 && (
              <div className="minicart-footer">
                <div className="minicart-subtotal">
                  <span>Subtotal:</span>
                  <strong>₹{calculateTotal()}</strong>
                </div>
                <div className="minicart-actions">
                  <motion.button
                    className="minicart-btn minicart-btn-secondary"
                    onClick={handleViewCart}
                    whileHover={{ scale: 1.02, y: -2 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    View Cart
                  </motion.button>
                  <motion.button
                    className="minicart-btn minicart-btn-primary"
                    onClick={handleCheckout}
                    whileHover={{ scale: 1.02, y: -2 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Checkout
                  </motion.button>
                </div>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default MiniCartDrawer;

